import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule, HttpParams } from '@angular/common/http';

import { EnvironmentService } from '../../../services/enviroment.service';
import { environment } from '../../../services/global-environment';;

interface SubjectsResponse {
  success: boolean;
  subjects: string[];
}

interface Question {
  id: string;
  SbjectId: string; // 注意此欄位名稱
  text: string;
  options: string[];
  correctAnswerIndex: number[]; // 已經是陣列，用來存儲多個正確答案索引
}

@Component({
  selector: 'app-question-mag',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule
  ],
  templateUrl: './question-mag.component.html',
  styleUrl: './question-mag.component.scss'
})
export class QuestionMagComponent implements OnInit {
  private apiUrl = environment.apiUrl;
  subjects: string[] = [];
  selectedSubject: string = '';
  searchKeyword: string = '';
  questions: Question[] = [];
  isLoading: boolean = false;

  // 編輯功能相關
  editingQuestion: Question | null = null;
  isEditModalOpen: boolean = false;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    // 載入科目名稱
    this.fetchSubjects();
  }

  fetchSubjects(): void {
    this.isLoading = true;
    const url = `${this.apiUrl}/api/examdb/getSubjects`;

    this.http.get<SubjectsResponse>(url).subscribe({
      next: (response) => {
        if (response && response.success && Array.isArray(response.subjects) && response.subjects.length > 0) {
          this.subjects = response.subjects;
          // Set the first subject as default
          this.selectedSubject = this.subjects[0];
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('無法取得科目清單:', error);
        this.isLoading = false;
        this.subjects = [];
      }
    });
  }

  loadQuestions(): void {
    // Modified to require subject selection
    if (!this.selectedSubject) {
      this.questions = [];
      return;
    }

    this.isLoading = true;

    // 使用 GET 方法並透過 URL 參數傳送查詢條件
    let params = new HttpParams();
    params = params.set('courseName', this.selectedSubject);

    if (this.searchKeyword) {
      params = params.set('keyword', this.searchKeyword);
    }

    const url = `${this.apiUrl}/api/examdb/getQuestions`;

    this.http.get<any>(url, { params }).subscribe({
      next: (response) => {
        console.log('取得題目資料:', response);

        if (response && Array.isArray(response)) {
          this.questions = response;
        } else {
          this.questions = [];
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('無法取得題目資料:', error);
        this.questions = [];
        this.isLoading = false;
      }
    });
  }

  searchQuestions(): void {
    // Ensure subject is selected
    if (!this.selectedSubject) {
      return;
    }

    this.loadQuestions();
  }

  openEditModal(question: Question | null = null): void {
    console.log('開啟編輯視窗', question);

    if (question) {
      // 編輯現有題目 - 創建複本避免直接修改原始資料
      this.editingQuestion = JSON.parse(JSON.stringify(question));
    } else {
      // 新增題目
      this.editingQuestion = {
        id: '',
        SbjectId: this.selectedSubject || '',
        text: '',
        options: ['', '', '', ''],
        correctAnswerIndex: []
      };
    }

    this.isEditModalOpen = true;
    console.log('編輯狀態設置完成:', this.isEditModalOpen, this.editingQuestion);
  }

  closeEditModal(): void {
    this.isEditModalOpen = false;
    this.editingQuestion = null;
  }

  saveQuestion(): void {
    if (!this.editingQuestion) return;

    this.isLoading = true;
    const url = `${this.apiUrl}/api/examdb/${this.editingQuestion.id ? 'updateQuestion' : 'addQuestion'}`;

    // Create the payload with the correct structure according to API documentation
    const payload = {
      id: this.editingQuestion.id,
      SbjectId: this.editingQuestion.SbjectId, // Keep as SbjectId as specified in the API
      text: this.editingQuestion.text,
      options: this.editingQuestion.options,
      correctAnswerIndex: this.editingQuestion.correctAnswerIndex
    };

    console.log('儲存題目資料:', payload);

    // Send as JSON instead of FormData
    this.http.post<any>(url, payload).subscribe({
      next: (response) => {
        if (response && response.success) {
          // Successfully saved, reload questions
          this.loadQuestions();
        } else {
          console.error('儲存題目失敗:', response);
        }
        this.isLoading = false;
        this.closeEditModal();
      },
      error: (error) => {
        console.error('儲存題目時發生錯誤:', error);
        this.isLoading = false;
        this.closeEditModal();
      }
    });
  }

  // 設置正確答案 - 只支援單選，保留最後選的答案
  setCorrectAnswer(index: number): void {
    if (this.editingQuestion) {
      // 將 0 起始的索引值加 1
      const oneBasedIndex = index + 1;

      // 如果點擊已選中的答案，則不做任何變更（保持選中）
      // 如果點擊其他答案，則只保留這個新選中的答案
      if (!this.editingQuestion.correctAnswerIndex.includes(oneBasedIndex)) {
        // 清除所有先前的正確答案，只保留當前選中的答案
        this.editingQuestion.correctAnswerIndex = [oneBasedIndex];
        console.log('更新後的正確答案:', this.editingQuestion.correctAnswerIndex);
      }
    }
  }

  // 檢查是否為正確答案 - 考慮從 1 開始的索引
  isCorrectAnswer(index: number): boolean {
    if (!this.editingQuestion || !this.editingQuestion.correctAnswerIndex) {
      return false;
    }
    // 將 0 起始的索引值加 1，然後檢查是否匹配
    const oneBasedIndex = index + 1;
    return this.editingQuestion.correctAnswerIndex.includes(oneBasedIndex);
  }

  addOption(): void {
    if (this.editingQuestion) {
      this.editingQuestion.options.push('');
    }
  }

  removeOption(index: number, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.editingQuestion!.options.splice(index, 1);

    // If we're removing the correct answer, reset it
    if (this.isCorrectAnswer(index)) {
      this.editingQuestion!.correctAnswerIndex = [];
    } else if (this.editingQuestion!.correctAnswerIndex.includes(index)) {
      // Update correct answer index if needed
      const correctIndex = this.editingQuestion!.correctAnswerIndex.indexOf(index);
      this.editingQuestion!.correctAnswerIndex.splice(correctIndex, 1);
    }

    // Update other correct answer indices
    for (let i = 0; i < this.editingQuestion!.correctAnswerIndex.length; i++) {
      if (this.editingQuestion!.correctAnswerIndex[i] > index) {
        this.editingQuestion!.correctAnswerIndex[i]--;
      }
    }
  }

  // Prevent focus loss when modifying text
  onOptionBlur(event: FocusEvent, index: number): void {
    // Only handle real blur events, not programmatic ones
    if (event.relatedTarget !== document.getElementById('option-' + index)) {
      // Regular blur behavior if needed
    }
  }

  // Add a new method for deleting questions
  deleteQuestion(): void {
    if (!this.editingQuestion || !this.editingQuestion.id) return;

    if (confirm('確定要刪除此題目嗎？此操作無法復原。')) {
      this.isLoading = true;
      const url = `${this.apiUrl}/api/examdb/deleteQuestion`;

      // Send both id and SbjectId as required by the API
      const payload = {
        id: this.editingQuestion.id,
        SbjectId: this.editingQuestion.SbjectId
      };

      // Use DELETE method instead of POST
      this.http.delete<any>(url, {
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }).subscribe({
        next: (response) => {
          if (response && response.success) {
            // Successfully deleted, reload questions
            this.loadQuestions();
          } else {
            console.error('刪除題目失敗:', response);
          }
          this.isLoading = false;
          this.closeEditModal();
        },
        error: (error) => {
          console.error('刪除題目時發生錯誤:', error);
          this.isLoading = false;
          this.closeEditModal();
        }
      });
    }
  }
}
